import { useLayoutEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { Icon } from './icons'
import styles from './card-text.module.css'

export type CardTextFieldStyle = 'lead' | 'text' | 'quote'

export type CardTextField<K extends string> = {
  readonly key: K
  readonly title: string | null
  readonly placeholder: string
  readonly look: CardTextFieldStyle
  readonly max: number
}

export type CardTextValue<K extends string> = Readonly<Record<K, string>>

export type DailyTextKey = 'headline' | 'body' | 'attention' | 'advice' | 'question'
export type ThreeTextKey = 'body' | 'hint' | 'advice'

export const DAILY_TEXT_FIELDS: readonly CardTextField<DailyTextKey>[] = [
  { key: 'headline', title: null, placeholder: 'Заголовок', look: 'lead', max: 2000 },
  { key: 'body', title: 'Трактовка', placeholder: 'Трактовка карты', look: 'text', max: 5000 },
  { key: 'attention', title: 'На что обратить внимание', placeholder: 'На что обратить внимание', look: 'text', max: 2000 },
  { key: 'advice', title: 'Совет дня', placeholder: 'Совет дня', look: 'text', max: 2000 },
  { key: 'question', title: 'Вопрос себе', placeholder: 'Вопрос себе', look: 'quote', max: 2000 },
]

export const THREE_TEXT_FIELDS: readonly CardTextField<ThreeTextKey>[] = [
  { key: 'body', title: null, placeholder: 'Описание дня', look: 'text', max: 5000 },
  { key: 'hint', title: 'Подсказка', placeholder: 'Подсказка', look: 'text', max: 500 },
  { key: 'advice', title: 'Совет', placeholder: 'Совет', look: 'quote', max: 500 },
]

export function sameCardText<K extends string>(fields: readonly CardTextField<K>[], a: CardTextValue<K>, b: CardTextValue<K>): boolean {
  return fields.every((f) => a[f.key].trim() === b[f.key].trim())
}

function paragraphs(text: string): string[] {
  return text
    .trim()
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean)
}

const LOOK_CLASS: Record<CardTextFieldStyle, string | undefined> = {
  lead: styles.lead,
  text: styles.text,
  quote: styles.quote,
}

type EditableProps = {
  readonly value: string
  readonly placeholder: string
  readonly max: number
  readonly className: string | undefined
  readonly label: string
  readonly onChange: (next: string) => void
}

function EditableText({ value, placeholder, max, className, label, onChange }: EditableProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [length, setLength] = useState(value.length)
  useLayoutEffect(() => {
    const el = ref.current
    if (!el || document.activeElement === el) return
    if (el.innerText !== value) el.innerText = value
    setLength(value.length)
  }, [value])
  const read = () => {
    const el = ref.current
    if (!el) return
    let next = el.innerText.replace(/\n$/, '')
    if (next.length > max) {
      next = next.slice(0, max)
      el.innerText = next
    }
    setLength(next.length)
    onChange(next)
  }
  return (
    <div className={styles.editable}>
      <div
        ref={ref}
        className={`${className ?? ''} ${styles.field}`}
        contentEditable="plaintext-only"
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-label={label}
        data-placeholder={placeholder}
        onInput={read}
        onBlur={read}
      />
      {length > max * 0.8 && (
        <span className={styles.counter}>
          {length} / {max}
        </span>
      )}
    </div>
  )
}

type CardTextProps<K extends string> = {
  readonly fields: readonly CardTextField<K>[]
  readonly value: CardTextValue<K>
  readonly className?: string
  readonly onChange?: (next: CardTextValue<K>) => void
}

export function CardText<K extends string>({ fields, value, className, onChange }: CardTextProps<K>) {
  const editing = onChange !== undefined
  const lead = fields.filter((f) => f.look === 'lead')
  const rest = fields.filter((f) => f.look !== 'lead')
  const shown = editing ? rest : rest.filter((f) => paragraphs(value[f.key]).length > 0)
  const renderBody = (f: CardTextField<K>) =>
    editing ? (
      <EditableText
        value={value[f.key]}
        placeholder={f.placeholder}
        max={f.max}
        className={LOOK_CLASS[f.look]}
        label={f.title ?? f.placeholder}
        onChange={(next) => onChange({ ...value, [f.key]: next })}
      />
    ) : (
      paragraphs(value[f.key]).map((p, i) => (
        <p key={i} className={LOOK_CLASS[f.look]}>
          {p}
        </p>
      ))
    )
  return (
    <div className={className ? `${styles.root} ${className}` : styles.root} data-editing={editing || undefined}>
      {lead.map((f) => (editing || value[f.key].trim() ? <div key={f.key}>{renderBody(f)}</div> : null))}
      {shown.length > 0 && (
        <div className={styles.sections}>
          {shown.map((f) => (
            <section key={f.key} className={styles.section}>
              {f.title && <h2 className={styles.title}>{f.title}</h2>}
              {renderBody(f)}
            </section>
          ))}
        </div>
      )}
    </div>
  )
}

export type CardTextStatus = 'default' | 'changed'

type PreviewProps<K extends string> = {
  readonly fields: readonly CardTextField<K>[]
  readonly value: CardTextValue<K>
  readonly face?: ReactNode
  readonly eyebrow: string
  readonly name: string
  readonly status?: CardTextStatus | null
  readonly saving?: boolean
  readonly saved?: boolean
  readonly error?: string | null
  readonly onSave: (next: CardTextValue<K>) => Promise<unknown>
  readonly onReset?: () => void
  readonly resetting?: boolean
  readonly resetLabel?: string
  readonly readOnly?: boolean
  readonly saveLabel?: string
  readonly savedLabel?: string
  readonly footer?: ReactNode | ((editing: boolean) => ReactNode)
}

export function CardTextPreview<K extends string>({
  fields,
  value,
  face,
  eyebrow,
  name,
  status = null,
  saving = false,
  saved = false,
  error = null,
  onSave,
  onReset,
  resetting = false,
  resetLabel = 'Вернуть текст по умолчанию',
  readOnly = false,
  saveLabel = 'Сохранить',
  savedLabel = 'Сохранено',
  footer,
}: PreviewProps<K>) {
  const [draft, setDraft] = useState<CardTextValue<K> | null>(null)
  const editing = draft !== null
  const dirty = draft !== null && !sameCardText(fields, draft, value)
  const save = () => {
    if (!draft) return
    if (!dirty) {
      setDraft(null)
      return
    }
    onSave(draft).then(
      () => setDraft(null),
      () => undefined,
    )
  }
  return (
    <div className={styles.preview} data-editing={editing || undefined}>
      <div className={styles.bar}>
        <span className={styles.barLabel}>{editing ? 'Редактирование · нажмите на текст' : 'Так увидит пользователь'}</span>
        {status && !editing && (
          <span className={styles.badge} data-status={status}>
            {status === 'default' ? 'Текст по умолчанию' : 'Изменён'}
          </span>
        )}
        {!editing && !readOnly && (
          <button type="button" className={styles.pen} aria-label="Редактировать текст" title="Редактировать" onClick={() => setDraft(value)}>
            <Icon name="pen" size={18} />
          </button>
        )}
      </div>
      {face && <div className={styles.face}>{face}</div>}
      <div className={styles.heading}>
        <span className={styles.eyebrow}>{eyebrow}</span>
        <h3 className={styles.name}>{name}</h3>
      </div>
      <CardText fields={fields} value={draft ?? value} onChange={editing ? setDraft : undefined} />
      {editing ? (
        <div className={styles.actions}>
          <button type="button" disabled={saving} onClick={save}>
            {saving ? 'Сохраняю…' : saveLabel}
          </button>
          <button type="button" className="secondary" disabled={saving} onClick={() => setDraft(null)}>
            Отмена
          </button>
        </div>
      ) : (
        (onReset || saved) && (
          <div className={styles.actions}>
            {onReset && !readOnly && status === 'changed' && (
              <button type="button" className={`secondary ${styles.reset}`} disabled={resetting} onClick={onReset}>
                <Icon name="undo" size={16} />
                {resetting ? 'Возвращаю…' : resetLabel}
              </button>
            )}
            {saved && <span className={styles.note}>{savedLabel}</span>}
          </div>
        )
      )}
      {error && <p className={styles.error}>{error}</p>}
      {typeof footer === 'function' ? footer(editing) : footer}
    </div>
  )
}
